import { useCallback, useEffect, useState } from "react";
import { Platform, findNodeHandle } from "react-native";

const getFullscreenElement = () => {
  if (typeof document === "undefined") {
    return null;
  }

  return (
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.mozFullScreenElement ||
    document.msFullscreenElement ||
    null
  );
};

const requestElementFullscreen = async (element) => {
  if (!element) {
    return;
  }

  const requestMethod =
    element.requestFullscreen ||
    element.webkitRequestFullscreen ||
    element.mozRequestFullScreen ||
    element.msRequestFullscreen;

  if (!requestMethod) {
    return;
  }

  await requestMethod.call(element);
};

const exitDocumentFullscreen = async () => {
  if (typeof document === "undefined") {
    return;
  }

  const exitMethod =
    document.exitFullscreen ||
    document.webkitExitFullscreen ||
    document.mozCancelFullScreen ||
    document.msExitFullscreen;

  if (!exitMethod) {
    return;
  }

  await exitMethod.call(document);
};

export function useFullscreen(targetRef) {
  const isWeb = Platform.OS === "web";
  const isSupported =
    isWeb &&
    typeof document !== "undefined" &&
    typeof window !== "undefined";

  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    if (!isSupported) {
      return undefined;
    }

    const onFullscreenChange = () => {
      const activeElement = getFullscreenElement();
      const targetNode =
        targetRef?.current != null
          ? findNodeHandle(targetRef.current)
          : null;

      setIsFullscreen(Boolean(activeElement && targetNode && activeElement === targetNode));
    };

    onFullscreenChange();

    document.addEventListener("fullscreenchange", onFullscreenChange);
    document.addEventListener("webkitfullscreenchange", onFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", onFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", onFullscreenChange);
    };
  }, [isSupported, targetRef]);

  const enterFullscreen = useCallback(async () => {
    if (!isSupported || !targetRef?.current) {
      return;
    }

    try {
      const targetNode = findNodeHandle(targetRef.current);
      if (!targetNode) {
        return;
      }

      await requestElementFullscreen(targetNode);
    } catch (error) {
      console.warn("Failed to enter fullscreen:", error);
    }
  }, [isSupported, targetRef]);

  const exitFullscreen = useCallback(async () => {
    if (!isSupported) {
      return;
    }

    try {
      await exitDocumentFullscreen();
    } catch (error) {
      console.warn("Failed to exit fullscreen:", error);
    }
  }, [isSupported]);

  const toggleFullscreen = useCallback(async () => {
    if (isFullscreen) {
      await exitFullscreen();
      return;
    }

    await enterFullscreen();
  }, [enterFullscreen, exitFullscreen, isFullscreen]);

  return {
    isSupported,
    isFullscreen,
    enterFullscreen,
    exitFullscreen,
    toggleFullscreen,
  };
}
